import { Tooltip } from "antd";
import { CaretDownOutlined, PauseCircleOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import {
  KitchenHoldTimeOptions,
  KitchenHoldTimeText,
  kitchenHoldTimeLabel,
} from "./KitchenHoldTimeOptions";
import type { KitchenHoldControls } from "./useKitchenHoldChoice";

/**
 * Хапчето вдясно в заглавието на „Преглед на поръчката" (панелът и sheet-ът):
 * ⏸ и общото време на редовете — „Сега", „10′", „∞" (ръчно) — или „различно".
 * Кликът разгъва „Едно време за всички редове" (`KitchenHoldAllPicker`).
 * Без интернет е заключено с подсказка, като останалото „без интернет".
 */
export const KitchenHoldAllChip = ({ controls }: { controls: KitchenHoldControls }) => {
  const { t } = useTranslation();
  const { headerTime, allPickerOpen, onToggleAllPicker, lockedTooltip } = controls;
  const label =
    headerTime === "mixed"
      ? t("ordering.kitchenHold.mixed")
      : kitchenHoldTimeLabel(headerTime, t);
  const accessibleLabel = `${t("ordering.kitchenHold.allRowsLabel")}: ${label}`;

  const chip = (
    <button
      type="button"
      onClick={onToggleAllPicker}
      disabled={!!lockedTooltip}
      aria-expanded={allPickerOpen}
      aria-label={accessibleLabel}
      title={lockedTooltip ? undefined : accessibleLabel}
      className={`shrink-0 h-7 rounded-full border bg-pos-card px-2.5 inline-flex items-center gap-1 text-[12.5px] font-semibold text-pos-ink whitespace-nowrap cursor-pointer disabled:cursor-not-allowed disabled:opacity-45 ${
        allPickerOpen ? "border-pos-accent" : "border-pos-ctl"
      } ${lockedTooltip ? "pointer-events-none" : ""}`}
    >
      <PauseCircleOutlined className="text-[11px] text-pos-dim" />
      {headerTime === "mixed" ? (
        label
      ) : (
        <KitchenHoldTimeText time={headerTime} infinityClassName="w-[18px]" />
      )}
      <CaretDownOutlined className="text-[9px] text-pos-ink2" />
    </button>
  );

  // Заключено (локален режим): подсказката е на обвивката — disabled бутон не
  // получава mouse събития.
  return lockedTooltip ? (
    <Tooltip title={lockedTooltip}>
      <span className="inline-flex shrink-0 cursor-not-allowed">{chip}</span>
    </Tooltip>
  ) : (
    chip
  );
};

/** „Едно време за всички редове" — под заглавието, над редовете. При
 *  „различно" няма отбелязан избор. */
export const KitchenHoldAllPicker = ({ controls }: { controls: KitchenHoldControls }) => {
  const { t } = useTranslation();
  return (
    <div className="mx-3 rounded-xl bg-pos-panel px-2 pt-1.5 pb-2">
      <div className="mb-1.5 text-[12px] text-pos-dim">
        {t("ordering.kitchenHold.allRowsLabel")}
      </div>
      <KitchenHoldTimeOptions
        selected={controls.headerTime === "mixed" ? null : controls.headerTime}
        onPick={controls.onPickAll}
      />
    </div>
  );
};
